import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import trustDiffusionImage from './assets/projectCovers/TrustDiffusion.jpg';
import argMappingImage from './assets/projectCovers/ArgMapping.png';
import synthesiaImage from './assets/projectCovers/synthesiaImage.png';
import duilianVideo from './assets/videos/duilian.mp4';
import picassoVideo from './assets/videos/picasso.mp4';
import movieBarcodeData from './assets/movieBarcodeData';

const WAVE_DECAY    = 0.90
const WAVE_INJECT   = 0.10
const WAVE_SIGMA    = 5
const WAVE_CENTER_W = 5

const Container = styled.div`
  width: 100%;
  max-width: 100vw;
  margin: 0 auto;
  padding: 6rem 6rem;
  background: #ffffff;
  font-family: 'Inter', sans-serif;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding: 3rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const SectionHeader = styled.h3`
  font-size: 2rem;
  margin: 2rem 0 1rem 0;
  font-weight: 600;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  box-shadow: 0 1px 4px rgba(0,0,0,0.03);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ProjectImageContainer = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: pointer;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  min-height: 360px;
  
  > div:first-child {
    width: 100%;
    height: 360px;
    margin: -1rem;
    width: calc(100% + 2rem);
  }
`;

const ProjectImage = styled.img`
  max-width: 100%;
  max-height: 360px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const ProjectVideo = styled.video`
  max-width: 100%;
  max-height: 360px;
  width: auto;
  height: auto;
  object-fit: cover;
`;

const BarcodeCanvas = styled.canvas`
  width: 100%;
  height: 360px;
  display: block;
  image-rendering: pixelated;
`;

function MovieBarcode({ colors }) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const actsRef = useRef(new Float32Array(0));
  const rgbsRef = useRef([]);
  const hiRef = useRef(-1);
  const rafRef = useRef(null);
  
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (colors.length === 0) return;
    actsRef.current = new Float32Array(colors.length);
    rgbsRef.current = colors.map(({ hex }) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgb(${r},${g},${b})`;
    });
  }, [colors]);

  useEffect(() => {
    if (colors.length === 0) {
      cancelAnimationFrame(rafRef.current);
      actsRef.current.fill(0);
      const cvs = canvasRef.current;
      if (cvs) cvs.getContext('2d')?.clearRect(0, 0, cvs.width, cvs.height);
      return;
    }

    const tick = () => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      const acts = actsRef.current;
      const rgbs = rgbsRef.current;
      const n = acts.length;
      if (!canvas || !wrapper || n === 0) { 
        rafRef.current = requestAnimationFrame(tick); 
        return; 
      }

      // Inject gaussian pulse at cursor position
      const hi = hiRef.current;
      if (hi >= 0 && hi < n) {
        const iw = Math.ceil(WAVE_SIGMA * 3);
        for (let i = Math.max(0, hi - iw); i <= Math.min(n - 1, hi + iw); i++) {
          const d = i - hi;
          acts[i] = Math.min(1, acts[i] + Math.exp(-(d * d) / (2 * WAVE_SIGMA * WAVE_SIGMA)) * WAVE_INJECT);
        }
      }

      // Decay all activations
      for (let i = 0; i < n; i++) {
        acts[i] *= WAVE_DECAY;
        if (acts[i] < 0.003) acts[i] = 0;
      }

      // Sync canvas to wrapper dimensions
      const cW = wrapper.offsetWidth;
      const cH = wrapper.offsetHeight;
      if (canvas.width !== cW) canvas.width = cW;
      if (canvas.height !== cH) canvas.height = cH;

      // Draw all stripes; activated ones are wider
      const ctx = canvas.getContext('2d');
      const sw = cW / n;
      ctx.clearRect(0, 0, cW, cH);

      for (let i = 0; i < n; i++) {
        const act = acts[i];
        const wMult = 1 + (WAVE_CENTER_W - 1) * act;
        const sw2 = sw * wMult;
        ctx.fillStyle = rgbs[i];
        ctx.fillRect(i * sw - (sw2 - sw) / 2, 0, sw2, cH);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [colors]);

  const handleHover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = Math.min(Math.floor((x / rect.width) * colors.length), colors.length - 1);
    hiRef.current = idx;
    setHovered({ x, hex: colors[idx].hex });
  };

  const handleMouseLeave = () => {
    hiRef.current = -1;
    setHovered(null);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '360px' }}>
      <BarcodeCanvas ref={canvasRef} />
      <div
        onMouseMove={handleHover}
        onMouseLeave={handleMouseLeave}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor: 'pointer',
        }}
      >
        {hovered && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: `${Math.max(60, Math.min(hovered.x, (wrapperRef.current?.offsetWidth ?? 9999) - 60))}px`,
              transform: 'translateX(-50%)',
              background: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: '12px',
                height: '12px',
                background: hovered.hex,
                borderRadius: '2px',
              }}
            />
            {hovered.hex}
          </div>
        )}
      </div>
    </div>
  );
}

const CardBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardTitle = styled.h4`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const ProjectLabel = styled.div`
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #999;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const InfoButton = styled.button`
  background: transparent;
  border: 1px solid #000;
  color: #000;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
`;

const UseButton = styled.a`
  background: black;
  color: white;
  padding: 0.5rem 1rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ExpandedCard = styled.div`
  background: white;
  padding: 2rem;
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
`;

const ExpandedTitle = styled.h2`
  font-size: 2rem;
  margin: 0 0 1rem 0;
`;

const ThesisText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const ViewDemoButton = styled.a`
  display: inline-block;
  background: black;
  color: white;
  padding: 0.6rem 1.2rem;
  margin-top: 1rem;
  text-decoration: none;
`;

function normalizeUrl(url) {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
}

const projectsData = [
  {
    id: 0,
    title: 'Picasso',
    label: '“I paint objects as I think them, not as I see them."',
    url: 'https://picasso-make-art.vercel.app',
    video: picassoVideo,
    section: 'arttech',
    shortDescription: `Picasso is one of my favorite artists because of his philosophy towards art and life, which is that existence precedes essence. 
    This led to the artistic revolution that the presence of a person, or an object, is rarely portrayable through just their physical appearance. 
    Picasso would use symbolism, abstraction, and oftentimes distortion of fragments of his subject from different perspectives onto a 2D canvas. 
    This project is a small studio for us to practice the same technique and mentality.`
  },
  {
    id: 1,
    title: 'Movie barcode',
    label: 'A movie barcode compresses an entire film into a single image. Each vertical stripe is the dominant color of one scene — a film\'s complete visual palette, collapsed into one frame.',
    url: 'https://movie-barcodes.vercel.app/',
    barcodeColors: movieBarcodeData,
    section: 'arttech',
    shortDescription: `I saw that a company was selling these movie barcodes for almost a hundred dollars and wanted to make this so everyone can generate their own for free. A movie barcode compresses an entire film into a single image. Each vertical stripe is the dominant color of one scene — a film's complete visual palette, collapsed into one frame.
    We collect two types of colors: the dominant, or natural, color of each scene, setting the overall tone of the scene, and the most vivid color, which reveals the scene's most striking hue.

    Generate your own with the movie-barcode CLI.`,
  },
  {
    id: 2,
    title: 'Duilian - 对联🧧',
    label: 'Send your loved one your own digital duilian for the new years :)',
    url: 'https://send-duilian.vercel.app',
    video: duilianVideo,
    section: 'arttech',
    shortDescription: 'An experimental 对联 project — thumbnail is a looping video playing at 3x speed.',
  },
  {
    id: 3,
    title: 'Synthesia',
    label: 'Watch how artworks can be transformd into music',
    url: 'https://synthesia-music-art.vercel.app',
    image: synthesiaImage,
    section: 'arttech',
  },
  {
    id: 4,
    title: 'Trust Diffusion',
    label: 'Visualize uncertainty in AI-generated images',
    url: 'https://trust-diffusion.vercel.app/phase2_viewer/',
    image: trustDiffusionImage,
    section: 'arttech',
    shortDescription: 'Exploring how AI-generated imagery transforms our relationship with imagination, authenticity, and visual truth.',
    thesis: `Trust Diffusion examines how AI-generated imagery transforms our relationship with imagination, authenticity, and visual truth.`
  },
  {
    id: 5,
    title: 'Arg Mapping',
    label: 'INTERACTIVE TOOL',
    url: 'https://arg-mapping.vercel.app/canvas/ktxvbbaz3',
    image: argMappingImage,
    section: 'others',
    shortDescription: 'A canvas-first tool that helps teams structure debates by linking claims, evidence, counterarguments, and rebuttals into a living knowledge graph with AI-assistive features.',
  }
];

function ProjectItem({ project }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [revealed, setRevealed] = useState(false);

  return (
    <>
      <ProjectCard>
        {(project.video || project.image || project.barcodeColors) && (
          <ProjectImageContainer
            onClick={() => setIsExpanded(true)}
          >
            {project.video ? (
              <ProjectVideo src={project.video} autoPlay muted loop playsInline />
            ) : project.barcodeColors ? (
              <MovieBarcode colors={project.barcodeColors} />
            ) : (
              <ProjectImage src={project.image} alt={project.title} />
            )}
          </ProjectImageContainer>
        )}

        <CardBody>
          <CardTitle>{project.title}</CardTitle>
          <ProjectLabel>{project.label}</ProjectLabel>
          {project.reveal && !revealed && (
            <button
              onClick={e => { e.stopPropagation(); setRevealed(true); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#bbb',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'inherit',
                textTransform: 'uppercase',
                textDecoration: 'underline',
                textDecorationColor: '#ddd',
                textAlign: 'left',
              }}
            >
              reveal
            </button>
          )}
          {project.reveal && revealed && (
            <span style={{ color: '#c97a7a', fontSize: '0.75rem', fontStyle: 'italic' }}>
              {project.reveal}
            </span>
          )}
          <ButtonsRow>
            <InfoButton onClick={() => setIsExpanded(true)}>More info</InfoButton>
            <UseButton href={normalizeUrl(project.url)} target="_blank" rel="noopener noreferrer">Use project</UseButton>
          </ButtonsRow>
        </CardBody>
      </ProjectCard>

      {isExpanded && (
        <Overlay onClick={() => setIsExpanded(false)}>
          <ExpandedCard onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setIsExpanded(false)}>×</CloseButton>
            <ExpandedTitle>{project.title}</ExpandedTitle>
            <ThesisText>{project.shortDescription || project.thesis || 'No description available.'}</ThesisText>
            {project.thesis && <ThesisText>{project.thesis}</ThesisText>}
            <ViewDemoButton href={normalizeUrl(project.url)} target="_blank" rel="noopener noreferrer">USE PROJECT →</ViewDemoButton>
          </ExpandedCard>
        </Overlay>
      )}
    </>
  );
}

function Projects() {
  const artTech = projectsData.filter(p => p.section === 'arttech');
  const others = projectsData.filter(p => p.section === 'others');

  return (
    <Container>
      <SectionHeader>Art Tech</SectionHeader>
      <ProjectsGrid>
        {artTech.map(project => (
          <ProjectItem key={project.id} project={project} />
        ))}
      </ProjectsGrid>

      <SectionHeader>Others</SectionHeader>
      <ProjectsGrid>
        {others.map(project => (
          <ProjectItem key={project.id} project={project} />
        ))}
      </ProjectsGrid>
    </Container>
  );
}

export default Projects;
